import { Dropdown, Button, Input, Select } from 'antd';
import { CloseCircleFilled, SearchOutlined } from '@ant-design/icons';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ISearchKeywordResult, searchAll } from 'services/api/conversation.convList';
import {
  useSearchMsgListStatus,
  useSetSearchMsgListStatus,
} from 'hooks/business/searchMsgListStatus';
import { useSetSearchMsgWord } from 'hooks/business/searchMsgWord';
import posthog from '@leyan/lytics';
import { useMemoizedFn, useSize } from 'ahooks';
import { backendSearch2LocalAdapter } from 'utils/search';
import style from './SearchStyle';
import SearchWords, { CacheWord } from './SearchWords';
import SearchPage from './SearchPage';
import { useTranslate } from '@tolgee/react';
import insertAtCursor from 'utils/insertAtCursor';
import { uniq } from 'lodash-es';
import { useConversationToggleTextareaProps } from 'hooks/business/conversationsHooks/useConversationHotKey';
import { isValidConversationId } from '../Chat/utils';
import { usePrefetchConversations } from 'hooks/business/conversation';
import { logger } from 'services/logger';

const batchSearchMax = 50;

const supportBatchSearchTypes = ['order', 'track'];

type SearchMsgAllRtn = Conversation.SearchMsgAllRtn;
interface SearchProps {
  convWrap: HTMLDivElement;
}
function Search({ convWrap }: SearchProps) {
  const { t } = useTranslate();

  const [wrapHeight, setWrapHeight] = useState(0);
  const size = useSize(convWrap);

  useEffect(() => {
    setWrapHeight(size?.height || 0);
  }, [size]);

  const searchListStatus = useSearchMsgListStatus();
  const setSearchListStatus = useSetSearchMsgListStatus();
  const showHideSearchList = useCallback(
    (show: boolean) => () => {
      setSearchListStatus(show);
    },
    [setSearchListStatus],
  );

  const [inputValue, setInputValue] = useState('');
  const [searchWord, setSearchWord] = useState('');
  const [productIds] = useState<Conversation.SearchProdcutIds[]>([]);

  const setSearchMsgWord = useSetSearchMsgWord();
  const [searchTime, setSearchTime] = useState(Date.now());
  const inputEl = useRef<Input>(null);

  const [searchAllResult, setSearchAllResult] = useState<SearchMsgAllRtn>({} as SearchMsgAllRtn);

  const [showSearchHis, setShowSearchHis] = useState(true);
  const [showingTab, setShowingTab] = useState('all');
  const searchWrap = useRef<HTMLDivElement>(null);

  const [backendSearchLoading, setBackendSearchLoading] = useState(false);

  const prefetchConversations = usePrefetchConversations();

  const [isBatch, setIsBatch] = useState(false);
  const [isBatchFold, setIsBatchFold] = useState(false);
  const [error, setError] = useState('');

  const [prefetchConversationsMap, setPrefetchConversationsMap] = useState<
    Record<string, Conversation.Conversation>
  >({});

  useEffect(() => {
    if (!searchWord) return;

    setSearchAllResult({} as SearchMsgAllRtn);
    posthog.capture('$convSearch', {
      custRreport: {
        type: showingTab,
        searchWord,
      },
    });
    doBackendSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWord, showingTab]);

  const searchLabelMap = useMemo(() => {
    return {
      all: t('scene.chatWorkspace.conversationList.searchType.all'),
      order: t('scene.chatWorkspace.conversationList.searchType.orderNumber'),
      track: t('scene.chatWorkspace.conversationList.searchType.trackingNumber'),
      buyer: t('scene.chatWorkspace.conversationList.searchType.buyerNickname'),
      product: t('scene.chatWorkspace.conversationList.searchType.productName'),
      text: t('scene.chatWorkspace.conversationList.searchType.chatHistory'),
    };
  }, [t]);

  const searchOptions = useMemo(() => {
    return Object.keys(searchLabelMap).map((key) => {
      return {
        value: key,
        label: searchLabelMap[key],
      };
    });
  }, [searchLabelMap]);

  const prefetchSearchItemConversations = useCallback(
    async (conversationIds: string[]) => {
      // 预获取会话信息
      const conversations = await prefetchConversations(conversationIds);
      const m: Record<string, Conversation.Conversation> = {};
      conversations?.forEach((conversation, index) => {
        const _id = conversationIds[index];
        m[_id] = conversation;
      });
      setPrefetchConversationsMap(m);
    },
    [prefetchConversations],
  );

  const doSearch = useCallback(
    (searchStr: CacheWord = '') => {
      let str =
        (typeof searchStr === 'string' ? searchStr : searchStr.word) ||
        inputEl?.current?.input?.value ||
        '';

      let error = '';

      const isBatchCache = typeof searchStr !== 'string' && searchStr.type === 'batch';

      const _isBatch = isBatch || isBatchCache;

      if (_isBatch) {
        const splits = [' ', '，', '\r', '\n', ',', '	'];

        str = splits.reduce<string>((res, split) => {
          return uniq(res.split(split).filter((_) => _)).join(',');
        }, str || '');

        const _showingTab = isBatchCache ? searchStr.searchType || 'order' : showingTab;

        if (str.split(',').length > batchSearchMax) {
          if (_showingTab === 'order') {
            error = t('scene.chatWorkspace.conversationList.search.validation.maxOrderNumbers', {
              x: batchSearchMax,
            });
          } else if (_showingTab === 'track') {
            error = t('scene.chatWorkspace.conversationList.search.validation.maxTrackingNumbers', {
              x: batchSearchMax,
            });
          }
        }

        if (isBatchCache) {
          setShowingTab(_showingTab);
        }

        if (str.length) {
          setIsBatchFold(true);
        }
      }

      setSearchWord(str.trim());
      setSearchMsgWord(str.trim());
      setSearchTime(Date.now());
      setInputValue(str);
      setShowSearchHis(str === '' || error.length > 0);
      setError(error);
      setIsBatch(_isBatch);
    },
    [inputEl, setSearchMsgWord, isBatch, t, showingTab],
  );

  const doBackendSearch = useMemoizedFn(async () => {
    if (!searchWord) return;

    if (showingTab === 'all') {
      setBackendSearchLoading(true);
      try {
        const result = await searchAll({
          keyword: searchWord,
          pageSize: 3,
          suTalkMode: true,
        });
        const {
          buyerSearchResult,
          orderSearchResult,
          trackSearchResult,
          itemSearchResult,
          msgSearchResult,
        } = result;

        let newResult = {} as SearchMsgAllRtn;
        const conversationIds: string[] = [];
        const updateResult = (type: string, result: ISearchKeywordResult) => {
          if (result.searchRow.length) {
            const rows: Conversation.SearchItemUI[] = [];
            result.searchRow.forEach((row) => {
              // 预获取会话信息
              if (isValidConversationId(row.conversationId)) {
                conversationIds.push(row.conversationId);
              }
              rows.push(backendSearch2LocalAdapter(row));
            });
            newResult = {
              ...newResult,
              [type]: {
                rows,
                offsetTime: result.nextOffset,
                offsetNum: result.hasNext ? 1 : 0,
              },
            };
          }
        };

        updateResult('buyer', buyerSearchResult);
        updateResult('order', orderSearchResult);
        updateResult('track', trackSearchResult);
        updateResult('product', itemSearchResult);
        updateResult('text', msgSearchResult);

        setSearchAllResult(newResult);

        await prefetchSearchItemConversations(conversationIds);
      } catch (err: any) {
        logger.error(`[doBackendSearch] Failed to search: ${err?.message}`);
      } finally {
        setBackendSearchLoading(false);
      }
    }
  });

  const errorRef = useRef<HTMLDivElement>(null);
  const { height: errorHeight = 0 } = useSize(errorRef) || {};

  const SearchHistory = (
    <div className="ant-dropdown-menu ant-dropdown-menu-root ant-dropdown-menu-vertical ant-dropdown-menu-light">
      <div
        ref={searchWrap}
        className={style.searchWrap}
        style={{
          height:
            wrapHeight - (isBatch ? (isBatchFold ? 49 + (error ? errorHeight : 0) : 137) : 48),
        }}
      >
        <SearchWords
          showSearchHis={showSearchHis}
          searchWord={searchWord}
          isBatch={isBatch}
          searchType={showingTab}
          doSearch={doSearch}
        />
        {!showSearchHis && (
          <SearchPage
            type={showingTab}
            searchAllResult={searchAllResult}
            setShowingTab={setShowingTab}
            searchWord={searchWord}
            isBatchSearch={isBatch}
            time={searchTime}
            productIds={showingTab === 'product' ? productIds : undefined}
            loading={backendSearchLoading}
            prefetchConversationsMap={prefetchConversationsMap}
            onPrefetchConversations={prefetchSearchItemConversations}
          />
        )}
      </div>
    </div>
  );

  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const { width: selectWidth = 0 } = useSize(selectWrapperRef) || {};

  const typeSelect = (
    <Select
      dropdownMatchSelectWidth={false}
      value={showingTab}
      onChange={(showingTab) => {
        setShowingTab(showingTab);
        setIsBatch(false);
        setIsBatchFold(false);
      }}
      options={searchOptions}
    />
  );

  const onChange = useCallback(
    (value: string) => {
      setError('');
      setInputValue(value);
      if (value === '') {
        setShowSearchHis(true);
        setSearchWord('');
        setSearchMsgWord('');
      }
    },
    [setSearchMsgWord],
  );

  const close = (
    <CloseCircleFilled
      key="closeBtn"
      onClick={() => {
        onChange('');
        setIsBatchFold(false);
      }}
    />
  );

  const searchButton = (
    <Button
      key="searchBtn"
      size="small"
      type="text"
      onClick={() => {
        setSearchListStatus(true);
        doSearch(inputValue);
      }}
    >
      <SearchOutlined style={{ color: 'var(--fett-text-tertiary)', cursor: 'pointer' }} />
    </Button>
  );

  const cancelButton = searchListStatus && (
    <Button
      size="small"
      type="link"
      onClick={() => {
        setSearchListStatus(false);
        onChange('');
        setIsBatch(false);
        setIsBatchFold(false);
      }}
    >
      {t('common.action.cancel')}
    </Button>
  );

  const placeholder = isBatch
    ? t('scene.chatWorkspace.conversationList.search.placeholder.batchSearch', {
        x: batchSearchMax,
      })
    : showingTab === 'all'
    ? t('scene.chatWorkspace.conversationList.placeholder.searchKeyword')
    : supportBatchSearchTypes.includes(showingTab)
    ? t('scene.chatWorkspace.conversationList.placeholder.batchSearch')
    : searchLabelMap[showingTab as keyof typeof searchLabelMap];

  const conversationToggleTextAreaProps = useConversationToggleTextareaProps();

  return (
    <Dropdown
      visible={searchListStatus}
      placement="bottomLeft"
      className={style.searchIptWrap}
      getPopupContainer={() => convWrap}
      overlay={SearchHistory}
      overlayClassName={style.searchOverlay}
    >
      {isBatch ? (
        <div className={style.batchSearch}>
          <div className={style.searchIpt}>
            <section ref={selectWrapperRef}>{typeSelect}</section>
            <Input.TextArea
              style={{ paddingLeft: selectWidth + 4 }}
              autoFocus={!isBatchFold}
              rows={isBatchFold ? 1 : 5}
              placeholder={placeholder}
              value={inputValue}
              {...conversationToggleTextAreaProps}
              onFocus={() => {
                setIsBatchFold(false);
                conversationToggleTextAreaProps.onFocus();
              }}
              onClick={() => setIsBatchFold(false)}
              onChange={(e) => onChange(e.target.value)}
              onPressEnter={(e) => {
                e.preventDefault();

                const textarea = e.target as HTMLTextAreaElement;

                if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
                  const nextValue = insertAtCursor(textarea, '\n');

                  onChange(nextValue);

                  requestAnimationFrame(() => {
                    textarea?.blur();
                    textarea?.focus();
                  });
                } else {
                  doSearch(textarea.value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  e.stopPropagation();

                  const textarea = e.target as HTMLTextAreaElement;

                  const nextValue = insertAtCursor(textarea, '	');

                  onChange(nextValue);

                  requestAnimationFrame(() => {
                    textarea?.blur();
                    textarea?.focus();
                  });
                }
              }}
            />
            {close}
            {searchButton}
            {isBatchFold && error.length > 0 && <div ref={errorRef}>{error}</div>}
          </div>
          {cancelButton}
        </div>
      ) : (
        <div className="flex items-center">
          <div className={style.searchIpt}>
            <Input
              maxLength={32}
              size="small"
              ref={inputEl}
              value={inputValue}
              onClick={showHideSearchList(true)}
              onDoubleClick={() => supportBatchSearchTypes.includes(showingTab) && setIsBatch(true)}
              onChange={(e) => onChange(e.target.value)}
              onPressEnter={() => doSearch()}
              title={placeholder}
              placeholder={placeholder}
              suffix={[inputValue && close, searchButton]}
              addonBefore={typeSelect}
            />
          </div>
          {cancelButton}
        </div>
      )}
    </Dropdown>
  );
}

export default Search;
